import {
  Avatar,
  Button,
  Icon,
  IconButton,
  TextCaption,
  Tooltip,
} from '@pypestream/design-system';
import { FC, memo, useMemo, useState } from 'react';

import { useRepublishModal } from '../../components';
import { formatDateTime } from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import type { SolutionVersion } from '../../xstate/prostudio.api-service.types';
import { filterUniqueVersions, mergeVersions } from '../versions';
import { AgentChanges, CountChips } from './diff-parts';
import { diffSolutions, totalChanges, versionNumber } from './solution-diff';
import { useSolutionVersionId, useVersionRows } from './use-version-rows';

type MergedVersion = SolutionVersion & { env: string };

/** A past version's changes against the version published before it. */
const VersionDetail: FC<{
  version: string;
  previous: string | null;
}> = ({ version, previous }) => {
  const toId = useSolutionVersionId();
  const current = useVersionRows(toId(version));
  const before = useVersionRows(previous ? toId(previous) : null);

  if (current.status === 'error' || before.status === 'error') {
    return (
      <TextCaption variant="danger">
        Couldn&apos;t load this version&apos;s nodes:{' '}
        {current.error ?? before.error}
      </TextCaption>
    );
  }
  if (!current.rows || (previous && !before.rows)) {
    return <TextCaption variant="secondary">Loading changes…</TextCaption>;
  }

  const diff = diffSolutions(before.rows ?? [], current.rows);
  const total = totalChanges(diff.counts);
  return (
    <div className="c-deploy-version__detail">
      <div className="c-deploy-eyebrow">
        {previous
          ? `What changed since ${previous}, by agent`
          : 'First published version'}
      </div>
      <div className="c-deploy-panel__sub">
        <TextCaption variant="secondary">
          {total} node{total === 1 ? '' : 's'}{' '}
          {previous ? 'changed' : 'published'}
        </TextCaption>
        <CountChips counts={diff.counts} />
      </div>
      <AgentChanges agents={diff.agents} />
    </div>
  );
};

/**
 * How many versions are drawn at a time. Every row is several design-system
 * components (an avatar, two tooltips, two buttons), and a solution can have
 * over a thousand versions: drawing HBO Max's 1,045 at once froze the page
 * for 35 seconds. The newest come first, and "Show more" adds the next ones.
 */
export const VERSIONS_PAGE = 20;

const PublishedVersionsList: FC<{ isViewer: boolean }> = ({ isViewer }) => {
  // One selector per field: an object literal is a new reference on every
  // machine transition, which would redraw the whole list each time.
  const versions = useProStudioCtxSelector((ctx) => ctx.versions);
  const deployedVersions = useProStudioCtxSelector(
    (ctx) => ctx.deployedVersions
  );
  const [open, setOpen] = useState<string | null>(null);
  const [shown, setShown] = useState(VERSIONS_PAGE);
  const [republishing, setRepublishing] = useState<SolutionVersion>();
  const { republishModal, openModal: openRepublishModal } = useRepublishModal({
    botVersion: republishing,
  });

  const list = useMemo(
    () =>
      (
        mergeVersions(filterUniqueVersions(versions || [])) as MergedVersion[]
      ).sort((a, b) => versionNumber(b.version) - versionNumber(a.version)),
    [versions]
  );

  const isCurrent = (version: string | null, env: string) =>
    !!version && deployedVersions?.[env as 'sandbox' | 'live'] === version;

  return (
    <div className="c-deploy-versions" data-testid="bobcat-deploy-versions">
      <div className="c-deploy-eyebrow">Published versions</div>
      {list.length > 0 && (
        <div className="c-deploy-version__row c-deploy-version__row--head">
          <span className="c-deploy-version__toggle">Version</span>
          <span className="c-deploy-version__envs">Environment</span>
          <span className="c-deploy-version__by">Last published</span>
          <span className="c-deploy-version__actions" />
        </div>
      )}
      {!list.length && (
        <TextCaption variant="secondary">
          Nothing published yet. Publish the draft to create the first version.
        </TextCaption>
      )}
      {list.slice(0, shown).map((item, i) => {
        const key = item.version ?? String(i);
        const isOpen = open === key;
        const previous = list[i + 1]?.version ?? null;
        const name = `${item.first_name} ${item.last_name}`.trim();
        return (
          <div
            key={key}
            className="c-deploy-version"
            data-testid="bobcat-deploy-version"
          >
            <div className="c-deploy-version__row">
              <button
                type="button"
                className="c-deploy-version__toggle"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : key)}
              >
                <Icon
                  name={isOpen ? 'chevron-down' : 'chevron-right'}
                  size="xsmall"
                />
                {item.version}
              </button>
              <span className="c-deploy-version__envs">
                {item.env.split(', ').map((env) => (
                  <span
                    key={env}
                    className={`c-deploy-env-tag c-deploy-env-tag--${env}${
                      isCurrent(item.version, env) ? ' is-current' : ''
                    }`}
                  >
                    <span className="c-deploy-env__dot" />
                    {env === 'live' ? 'Live' : 'Sandbox'}
                  </span>
                ))}
              </span>
              <span className="c-deploy-version__by">
                {name && (
                  <Avatar size="small" src={item.picture} label={name} />
                )}
                <span>
                  {name}
                  {name && item.time ? ' · ' : ''}
                  {item.time ? formatDateTime(item.time) : ''}
                </span>
              </span>
              <span className="c-deploy-version__actions">
                {!isViewer && (
                  <Tooltip content="Republishing a version will not replace nodes on the workflows page">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setRepublishing(item);
                        openRepublishModal();
                      }}
                    >
                      <Icon name="deploy" slot="prefix" size="xsmall" />
                      Republish
                    </Button>
                  </Tooltip>
                )}
                <Tooltip content="Download solution">
                  <IconButton
                    name="download"
                    size="small"
                    onClick={() =>
                      sendProStudioEvent({
                        type: 'getVersionFile',
                        version: item.version ?? null,
                      })
                    }
                  />
                </Tooltip>
              </span>
            </div>
            {isOpen && item.version && (
              <VersionDetail version={item.version} previous={previous} />
            )}
          </div>
        );
      })}
      {list.length > shown && (
        <button
          type="button"
          className="c-deploy-version__more"
          data-testid="bobcat-deploy-versions-more"
          onClick={() => setShown((n) => n + VERSIONS_PAGE)}
        >
          Show {Math.min(VERSIONS_PAGE, list.length - shown)} more
          <span className="c-deploy-version__more-total">
            {shown} of {list.length} versions shown
          </span>
        </button>
      )}
      {republishModal}
    </div>
  );
};

/** Memoized: Deploy redraws on every draft edit, and the list doesn't change. */
export const PublishedVersions = memo(PublishedVersionsList);
